import { Grid, Theme, Typography } from "@mui/material";
import { INewGroundEvent } from "@vmosolutions/oc-interfaces";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { formatDateString } from "src/components/DateDisplay";
import { EventChip } from "src/components/Flights/EventChip";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState } from "src/redux/reducers/StructuresReducer";
import { EventTypes } from "../FlightBuilder";
import { getBlockDurationString } from "./FlightRowEdit";
import { validateGroundEvent } from "./GroundEventRowEdit";

interface IGroundEventRowDisplayProps {
  event: INewGroundEvent;
  legIndex: number;
  mosaicPane: HTMLElement;
  onSubmit: (eventValues: INewGroundEvent) => void;
  theme: Theme;
  eventType?: EventTypes;
  displayAsUTC: boolean;
  setEditRow: React.Dispatch<React.SetStateAction<number>>;
  setFocusField: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * Show selected Flight Row in Edit Mode
 * @param props -
 */
export const GroundEventRowDisplay = (props: IGroundEventRowDisplayProps): JSX.Element => {
  const { event, legIndex, theme, eventType, displayAsUTC, setEditRow, setFocusField } = props;

  // Structure dependencies
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;

  const locationTZ = useMemo(
    () => (!!airportOptions && event.location && !displayAsUTC ? airportOptions[event.location].timezoneId : "Etc/UTC"),
    [airportOptions, event.location, displayAsUTC]
  );

  const blockDuration = getBlockDurationString(event?.scheduledStart, event?.scheduledEnd);
  const isValid = Object.keys(validateGroundEvent(event)).length === 0;
  //const bgColor1 = event?.eventType?.color ? `#${event?.eventType?.color}` : theme.custom.forms.inputBack;
  //const bgColor2 = LightenDarkenColor(bgColor1, -0.15);
  //const contrastText = theme.palette.getContrastText(bgColor1);
  //const background = `repeating-linear-gradient(135deg, ${bgColor1}, ${bgColor1} 10px, ${bgColor2} 10px, ${bgColor2} 20px)`;
  //const borderStyle = `1px dashed ${contrastText}`;

  const handleSetFocusField = useCallback(
    (field: string) => (event: React.MouseEvent) => {
      setFocusField(field);
      setEditRow(legIndex);
    },
    [setFocusField, setEditRow, legIndex]
  );

  return (
    <Grid item>
      <Grid className={"form_styles"}>
        <Grid container direction="row" className="flight_row">
          <Grid container direction="row" className="crucial_fields" flexWrap="nowrap">
            <Grid item className="checkbox">
              <CheckboxField
                checked={false}
                onChange={() => {
                  setFocusField(null);
                  setEditRow(legIndex);
                }}
              />
            </Grid>
            <Grid item className="flight_leg">
              <Typography variant="caption">{(legIndex + 1).toString()}</Typography>
            </Grid>
            <Grid
              container
              direction="row"
              className={`required_fields ${isValid ? "" : "invalid"}`}
              flexWrap="nowrap"
              alignItems="center"
            >
              <Grid item style={{ width: 130 }} sx={{ marginTop: "-4px" }}>
                <EventChip data={event} />
              </Grid>
              <Grid
                item
                onMouseDown={handleSetFocusField("eventType")}
                sx={{
                  padding: "2px 8px",
                  margin: "5px",
                  borderRadius: "0px",
                  height: "24px",
                  width: "100px",
                  fontSize: "12px",
                  textAlign: "center",
                  lineHeight: "20px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  backgroundColor: theme.custom.forms.inputBack,
                  color: theme.custom.forms.inputText,
                }}
              >
                {event?.eventType ? `${event?.eventType?.title ?? ""} - ${event?.eventType?.description ?? ""}` : ""}
              </Grid>
              <Grid
                item
                onMouseDown={handleSetFocusField("location")}
                sx={{
                  padding: "2px 8px",
                  margin: "5px",
                  height: "24px",
                  width: "100px",
                  fontSize: "12px",
                  lineHeight: "20px",
                  backgroundColor: theme.custom.forms.inputBack,
                  color: theme.custom.forms.inputText,
                }}
              >
                {event?.location ?? ""}
              </Grid>
              <Grid
                item
                onMouseDown={handleSetFocusField("scheduledStart_d_input")}
                sx={{
                  padding: "2px 8px",
                  margin: "5px",
                  borderRadius: "0px",
                  height: "24px",
                  width: "163px",
                  fontSize: "12px",
                  textAlign: "center",
                  lineHeight: "20px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  backgroundColor: theme.custom.forms.inputBack,
                  color: locationTZ === "Etc/UTC" ? theme.custom.forms.inputText : theme.palette.secondary.light,
                }}
              >
                {event?.scheduledStart ? formatDateString(parseInt(event?.scheduledStart), locationTZ) : ""}
              </Grid>
              <Grid
                item
                onMouseDown={handleSetFocusField("scheduledEnd_d_input")}
                sx={{
                  padding: "2px 8px",
                  margin: "5px",
                  borderRadius: "0px",
                  height: "24px",
                  width: "163px",
                  fontSize: "12px",
                  textAlign: "center",
                  lineHeight: "20px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  backgroundColor: theme.custom.forms.inputBack,
                  color: locationTZ === "Etc/UTC" ? theme.custom.forms.inputText : theme.palette.secondary.light,
                }}
              >
                {event?.scheduledEnd ? formatDateString(parseInt(event?.scheduledEnd), locationTZ) : ""}
              </Grid>
              <Typography className="block_duration" variant="caption">
                {blockDuration}
              </Typography>
              <Grid
                item
                onMouseDown={handleSetFocusField("aircraft")}
                sx={{
                  padding: "2px 8px",
                  margin: "5px 5px 5px 10px",
                  borderRadius: "0px",
                  height: "24px",
                  width: "100px",
                  fontSize: "12px",
                  textAlign: "center",
                  lineHeight: "20px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  backgroundColor: theme.custom.forms.inputBack,
                  color: theme.custom.forms.inputText,
                }}
              >
                {event?.aircraft?.registration
                  ? `${event?.aircraft.registration} (${
                      event?.aircraft?.aircraftType && event?.aircraft?.aircraftType?.title
                    })`
                  : ""}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
